import type { ChangeEvent, SelectHTMLAttributes } from 'react';
import { type FieldShellProps, FormField } from './FormField';

interface SelectOption<TValue extends string> {
  value: TValue;
  label: string;
}

type NativeProps = Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  'id' | 'className' | 'required' | 'value' | 'onChange'
>;

/**
 * With a placeholder the empty choice is a real answer, so `onChange` can
 * receive `''`; without one it only ever receives an option's own value.
 */
type ChoiceProps<TValue extends string> =
  | {
      /** Leading empty option, e.g. "Select gender". */
      placeholder: string;
      value: TValue | '';
      onChange: (value: TValue | '') => void;
    }
  | {
      placeholder?: undefined;
      value: TValue;
      onChange: (value: TValue) => void;
    };

type SelectProps<TValue extends string> = FieldShellProps &
  NativeProps &
  ChoiceProps<TValue> & {
    options: readonly SelectOption<TValue>[];
  };

/**
 * A labelled `<select>` that reports the chosen option's value, typed as the
 * options' own value type — so an enum-valued select needs no cast.
 */
export function Select<TValue extends string>(props: SelectProps<TValue>) {
  const {
    label,
    hideLabel,
    error,
    helpText,
    required,
    className,
    options,
    placeholder,
    value,
    onChange: _onChange,
    ...select
  } = props;

  function handleChange(event: ChangeEvent<HTMLSelectElement>) {
    const chosen = options.find((option) => option.value === event.target.value);
    if (chosen) props.onChange(chosen.value);
    else if (props.placeholder !== undefined) props.onChange('');
  }

  return (
    <FormField
      label={label}
      hideLabel={hideLabel}
      error={error}
      helpText={helpText}
      required={required}
      className={className}
    >
      {({ id, describedBy, invalid }) => (
        <div className="c-select">
          <select
            {...select}
            id={id}
            value={value}
            onChange={handleChange}
            required={required}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            className="c-select__control"
          >
            {placeholder !== undefined && <option value="">{placeholder}</option>}
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      )}
    </FormField>
  );
}
